import i18n from '@/i18n';
import { dirFor } from '@/i18n/dir';
import type { PublicTenant } from '@/api/types';

/**
 * Apply a clinic's branding to the running page.
 *
 * One static bundle is served on every clinic domain, so the identity is
 * applied at runtime rather than baked in at build time: colour, page title
 * and favicon all come from what the API said about this host.
 */
export function applyBranding(tenant: PublicTenant): void {
  const { branding, name } = tenant;

  document.title = branding.displayName || name;

  // The clinic's language drives `<html lang>`, `<html dir>` and the locale
  // `lib/format` formats in. Set the attributes now for the first paint and
  // hand the language to i18n, whose `languageChanged` handler keeps all three
  // in step from here on. A per-user preference, applied after login, changes
  // the language again through the same door. The clinic's chosen default
  // (`i18n.default`) wins over the platform `locale` when present.
  const lang = tenant.i18n?.default || tenant.locale || 'ro';
  document.documentElement.lang = lang;
  document.documentElement.dir = dirFor(lang);
  void i18n.changeLanguage(lang);

  if (branding.primaryColor && isSafeColor(branding.primaryColor)) {
    const root = document.documentElement;
    root.style.setProperty('--brand', branding.primaryColor);
    // Pick the text colour that will actually be readable on it, rather than
    // assuming white and hoping the clinic did not choose yellow.
    root.style.setProperty('--brand-contrast', readableTextOn(branding.primaryColor));
  }

  if (branding.faviconUrl) setFavicon(branding.faviconUrl);
}

/**
 * Only accept colours we can parse.
 *
 * The value arrives from configuration, and writing an arbitrary string into
 * a CSS custom property is a way to smuggle in more than a colour.
 */
function isSafeColor(value: string): boolean {
  return /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i.test(value.trim());
}

/**
 * Black or white, whichever has more contrast against the given colour.
 *
 * Uses relative luminance per WCAG rather than a naive brightness average,
 * because green and blue of the same numeric value are not equally light to
 * the eye.
 */
export function readableTextOn(hex: string): string {
  const { r, g, b } = hexToRgb(hex);
  const channel = (c: number) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  const luminance = 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
  // Contrast against white vs against black; take the better one.
  const onWhite = 1.05 / (luminance + 0.05);
  const onBlack = (luminance + 0.05) / 0.05;
  return onBlack > onWhite ? '#000000' : '#ffffff';
}

function hexToRgb(hex: string): { r: number; g: number; b: number } {
  let h = hex.trim().replace('#', '');
  if (h.length === 3)
    h = h
      .split('')
      .map((c) => c + c)
      .join('');
  const n = Number.parseInt(h, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

function setFavicon(href: string): void {
  let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  link.href = href;
}

/**
 * Culoarea clinicii, pentru documentele care se tipăresc.
 *
 * O tipărire își construiește propriul HTML într-o fereastră nouă, deci nu
 * moștenește nici variabilele CSS, nici stilurile paginii: fiecare fișier de
 * tipărire își scria culoarea la mână, și toate șapte scriau `#3f3f8f` —
 * indigo-ul Orthodonticăi. Clinica demo, al cărei brand e `#0f5ff5`, tipărea
 * chitanțe, planuri de tratament, fișe 043/e și bonuri de laborator în culoarea
 * altui client. Numele de pe hârtie era al ei; culoarea, nu.
 *
 * `applyBranding` a scris deja `--brand` pe rădăcină din configurația
 * tenantului, deci aici se citește de acolo — o singură sursă, aceeași pe ecran
 * și pe hârtie. Rezerva e albastrul implicit, nu al vreunei clinici anume.
 */
export function printBrandColor(): string {
  const fromRoot = getComputedStyle(document.documentElement).getPropertyValue('--brand').trim();
  return isSafeColor(fromRoot) ? fromRoot : '#0f5ff5';
}
